<template>
    <b-container fluid>
        <b-row>
            <b-col cols="12">
                <admin-page-header title="원청사 관리" description="Assessment 서비스를 이용하는 원청사 기업을 관리합니다."/>
            </b-col>
        </b-row>

        <b-row v-if="loading" class="admin-body-full-height">
            <b-col cols="12">
                <div class="d-flex align-items-center justify-content-center bgray-200 h-100">
                    <b-spinner class="align-middle mr-3"></b-spinner>
                    <h3 class="mb-0">Loading...</h3>
                </div>
            </b-col>
        </b-row>

        <b-row v-else class="assessment-panel mt-3">
            <b-col cols="12" class="h-md-100">
                <b-card no-body class="shadow-sm h-md-100 overflow-hidden" header-bg-variant="white">
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <h5 class="mb-0">원청사 리스트</h5>
                        </div>
                    </template>

                    <PageEmptyComponent v-if="loadError" compact :error="loadError" @retry="getClientsData"/>
                    <!-- 표 높이 = 화면 - (상단 바 48 · 제목 판 약 107 · 본문 위아래 16+16 · 카드 위 여백 16 · 카드 머리 약 50) -->
                    <b-table
                        v-else
                        :items="clients"
                        :fields="client_record"
                        table-class="ds-table ds-table-fixed admin-client-table"
                        empty-html="<h6 class='mt-3 gray-400'>조회된 원청사가 없습니다.</h6>"
                        show-empty
                        sticky-header="calc(100vh - 256px)"
                        class="mb-0"
                        no-border-collapse
                        >
                        <!-- 번호는 표시 순서 — 식별자가 아니다 -->
                        <template #cell(no)="row">
                            {{ row.index + 1 }}
                        </template>
                        <!-- 메인 칸 = 기업 상세 링크(원청사도 같은 기업 상세 화면) -->
                        <template #cell(company_name)="row">
                            <router-link :to="`/admin/company/${row.item.company_id}`" class="ds-detail-link ds-ellipsis" :title="row.value">{{ row.value }}</router-link>
                        </template>
                        <template #cell(company_code)="row">
                            <span class="ds-ellipsis" :title="row.value">{{ row.value || '-' }}</span>
                        </template>
                        <!-- 첫 코드 + '외 N개' 한 줄, 전체 목록은 title 로 -->
                        <template #cell(project_codes)="row">
                            <span class="ds-ellipsis" :title="codeList(row.value).join(', ')">{{ codeList(row.value)[0] || '-' }} <span v-if="codeList(row.value).length > 1" class="ds-sub">외 {{ codeList(row.value).length - 1 }}개</span></span>
                        </template>
                        <!-- 머리 이름은 화면 읽기 프로그램용(비우면 BootstrapVue 가 키를 영어로 읽는다) -->
                        <template #head(detail)="data">
                            <span class="sr-only">{{ data.label }}</span>
                        </template>
                        <template #cell(detail)="row">
                            <router-link :to="`/admin/company/${row.item.company_id}`" class="ds-row-icon-btn"
                                         :aria-label="`${row.item.company_name} 상세`" :title="`${row.item.company_name} 상세`">
                                <AppIcon name="chevron-right"/>
                            </router-link>
                        </template>
                    </b-table>
                </b-card>
            </b-col>
        </b-row>
    </b-container>
</template>                          
 

<script>
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import { getErrorMessage } from '@core/utils/api/errorMessage';

export default {
    name: 'AdminClient',
    components: { PageEmptyComponent },
    data() {
        return {
            loading: false,
            loadError: '',
            clients: [],

            // DS 표 규칙: No. → 메인(상세 링크) → 데이터 → 상세 아이콘. 행 44 고정이라 긴 값은 줄이고 title 로 전체를 보인다
            client_record: [
                { key: 'no', label: 'No.', thClass: 'ds-cell-no', tdClass: 'ds-cell-no' },
                { key: 'company_name', label: '기업명', sortable: true },
                { key: 'company_id', label: '기업 ID', sortable: true, thClass: 'text-right', tdClass: 'ds-cell-num', thStyle: { width: '100px' } },
                { key: 'company_code', label: '사업자등록번호', sortable: true, thStyle: { width: '160px' } },
                { key: 'project_codes', label: '연결 프로젝트', sortable: true, thStyle: { width: '240px' } },
                { key: 'detail', label: '상세', tdClass: 'text-right', thStyle: { width: '60px' } },
            ],
        }
    },
    async created () {
        await this.getClientsData()
    },
    methods: {
        // project_codes 는 백엔드 GROUP_CONCAT 쉼표 문자열
        codeList(value) {
            return (value || '').split(',').filter(Boolean);
        },
        async getClientsData() {
            this.loading = true;
            try {
                const response = await this.$axios.get('/company/clients');
                if (response.status === 200) {
                    this.clients = response.data;
                    this.loadError = '';
                }
            } catch (error) {
                console.error('Error searching users:', error);
                this.loadError = getErrorMessage(error, '원청사 목록을 불러오지 못했습니다.');
            } finally {
                this.loading = false;
            }
        }
    }
}
</script>

<style lang="scss">
// 고정 칸 합 624(No. 64 · 기업 ID 100 · 사업자등록번호 160 · 연결 프로젝트 240 · 상세 60) + 기업명 216 이상.
// 1366(메뉴 펼침) 본문 약 1074 에서 카드 테두리를 뺀 약 1072 안 — 그보다 좁으면 가로 스크롤
.admin-client-table {
    min-width: 840px;
}
</style>